import React from 'react';
import { SEO } from '../components/SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Building2, Globe, Shield, Wrench, Package, Award, Stethoscope } from 'lucide-react';
import SectionHeader from '../components/SectionHeader';

export const Services = () => {
  const services = [
    {
      title: 'FDA Regulatory Compliance',
      description: 'State-level FDA compliance - manufacturing licences, blood centre licences, testing lab licences, and inspection preparedness.',
      icon: <Shield size={32} />,
      link: '/services/fda',
    },
    {
      title: 'CDSCO Regulatory Compliance',
      description: 'New drug/device applications, NCE, FDC approvals, biological/biosimilar applications, medical device and NOC applications.',
      icon: <FileText size={32} />,
      link: '/services/cdsco',
    },
    {
      title: 'Global Regulatory Compliance',
      description: 'Strategic regulatory planning and dossier management for multi-product, multi-market registrations across pharma, devices, cosmetics, and food.',
      icon: <Globe size={32} />,
      link: '/services/global-regulatory',
    },
    {
      title: 'Revised Schedule M GMP Advisory',
      description: 'GMP gap analysis, SOP writing, audit readiness, and full compliance roadmaps aligned to Revised Schedule M regulations.',
      icon: <Shield size={32} />,
      link: '/services/gmp',
    },
    {
      title: 'WHO GMP Certification Advisory',
      description: 'Expert guidance for WHO GMP certification, pre-qualification support, and international GMP compliance for pharmaceutical manufacturers.',
      icon: <Award size={32} />,
      link: '/services/who-gmp',
    },
    {
      title: 'Specialized Regulatory Consultation',
      description: 'Specialized regulatory consulting for medical devices, cosmetic, nutraceutical, and food products.',
      icon: <Stethoscope size={32} />,
      link: '/services/specialized',
    },
    {
      title: 'Facility Design & Project Consultancy',
      description: 'GMP-compliant pharmaceutical facility design, cleanroom layout, HVAC engineering, and regulatory-ready infrastructure planning.',
      icon: <Building2 size={32} />,
      link: '/services/facility-design',
    },
    {
      title: 'Turnkey Key Project',
      description: 'End-to-end project management for pharma facility setup - from concept and design through construction, equipment procurement, and handover.',
      icon: <Wrench size={32} />,
      link: '/services/turnkey',
    },
    {
      title: 'Global Drug Sourcing',
      description: 'Procurement of innovator, branded, and reference listed drugs (RLDs) from regulated markets for BA/BE studies and clinical research.',
      icon: <Package size={32} />,
      link: '/services/drug-sourcing',
    },
  ];

  return (
    <div className="pt-[72px]">
      <SEO page="services" />
      {/* Hero */}
      <section
        className="relative py-24"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1705727210721-961cc64a6895?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #1570EF 0%, #1570EFee 35%, #1570EFaa 55%, transparent 75%)' }} />
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white max-w-2xl" >
            Comprehensive Regulatory & Consulting Services
          </h1>
          <p className="text-xl max-w-2xl text-white/90" >
            From first regulatory strategy to successful market approval - Pegasus Clinicare delivers integrated solutions that move your products forward, faster.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <p className="text-base sm:text-lg text-center max-w-4xl mx-auto" style={{ color: '#1E293B' }}>
            Navigating regulatory requirements across India and global markets requires more than procedural knowledge - it demands strategic thinking, technical precision, and a partner who understands your industry from the inside. Pegasus Clinicare offers a complete portfolio of regulatory, compliance, infrastructure, and sourcing services designed to support your business at every stage of the product lifecycle.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24" style={{ backgroundColor: '#EFF6FF' }}>
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader
            label="OUR SERVICES"
            title="Expert-Led Solutions for Every Stage"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                className="bg-white p-8 rounded-2xl border border-[#BFDBFE] hover:border-[#1570EF] hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-16 h-16 bg-[#1570EF] rounded-full flex items-center justify-center mb-6">
                  <div className="text-white">{service.icon}</div>
                </div>
                <h3 className="text-xl font-bold mb-4" style={{ color: '#1B3A6B' }}>
                  {service.title}
                </h3>
                <p className="text-sm mb-6" style={{ color: '#1E293B' }}>
                  {service.description}
                </p>
                <Link
                  to={service.link}
                  className="inline-flex items-center gap-2 text-[#1570EF] font-semibold text-sm hover:gap-3 transition-all duration-200"
                >
                  Learn More
                  <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/contact"
              data-testid="request-consultation-cta"
              className="inline-block px-8 py-4 bg-[#1570EF] text-white font-semibold rounded-full hover:bg-[#1E40AF] hover:scale-105 transition-all duration-200"
            >
              Request a Consultation for Any Service
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
